/**
 * AI Service — calls FastAPI backend
 *
 * Routing strategy:
 *   - First "quick insight" for session-prep, paper-insight, report, networking,
 *     recap uses the dedicated specialized API route for richer structured output.
 *   - All conversational follow-ups route through ai-chat (agentic tool calling).
 *   - ai-chat streaming uses apiClient.stream + ReadableStream for SSE.
 */
import apiClient, { ApiError } from './apiClient';
import authService from './authService';
import { AISharingRequired, withAISharing, aiSharingRequestOptions, invalidateAISharing } from './aiSharingService';
import type { ApiRequestOptions } from './apiClient';
import type {
  AIContext,
  AIResponse,
  ActionChip,
  ConferenceRecap,
  EventModuleKey,
  AIMode,
} from '@conference/shared';

export type { AIMode, AIContext, AIResponse, ActionChip, ConferenceRecap, RecapSection, RecapItem, RecapStats } from '@conference/shared';

export function formatCopilotError(error: unknown): AIResponse {
  if (error instanceof ApiError && (error.code === 'ai_sharing_required' || error.code === 'ai_client_update_required')) invalidateAISharing();
  if (error instanceof AISharingRequired) return { content: error.message, chips: [] };
  if (error instanceof ApiError && error.status === 429) {
    const retrySuffix = error.retryAfterSeconds
      ? ` Try again in ${error.retryAfterSeconds} second${error.retryAfterSeconds === 1 ? '' : 's'}.`
      : ' Please try again shortly.';
    const baseMessage = error.code === 'copilot_busy'
      ? 'Copilot is busy.'
      : error.message || 'Copilot is temporarily unavailable.';
    const busyRetrySuffix = error.code === 'copilot_busy' && error.retryAfterSeconds
      ? ` Please try again in ${error.retryAfterSeconds} second${error.retryAfterSeconds === 1 ? '' : 's'}.`
      : retrySuffix;
    return {
      content: `${baseMessage.replace(/[. ]+$/, '')}.${busyRetrySuffix}`,
      chips: [{ label: 'Retry', icon: 'RefreshCw', action: 'retry' }],
    };
  }

  const message = error instanceof Error ? error.message : String(error);
  const content = message.includes('model') || message.includes('Model')
    ? 'The AI model is temporarily unavailable. Please try again later or contact the organizer.'
    : message.includes('API key') || message.includes('api_key') || message.includes('Incorrect API key')
    ? 'The AI service is not configured correctly. Please contact the organizer.'
    : message.includes('Unauthorized') || message.includes('401')
    ? 'Authentication error — please sign out and back in.'
    : message.includes('Failed to send') || message.includes('fetch')
    ? 'Could not reach the AI service. Check that the API server is running.'
    : `AI error: ${message}`;

  return {
    content,
    chips: [{ label: 'Retry', icon: 'RefreshCw', action: 'retry' }],
  };
}

// ─── Mode Config ───────────────────────────────────────────────────────────────

export const MODE_CONFIG = {
  general: {
    label: 'AI Copilot',
    icon: 'Sparkles',
    placeholder: 'Ask AI anything about the conference…',
    greeting: "Hi! I'm your AI Conference Copilot. I can answer questions using the features and content available for this event. How can I help?",
    suggestions: [
      'Recommend sessions based on my interests',
      'Who should I network with today?',
      'What are the trending topics this year?',
      'Help me plan my conference schedule',
    ],
  },
  'session-prep': {
    label: 'Session Prep',
    icon: 'Zap',
    placeholder: 'Ask about this session…',
    greeting: "Let me help you prepare for this session using the event content currently available. Where should we start?",
    suggestions: [
      'What should I know before attending?',
      'Generate questions to ask the speaker',
      'What papers should I read first?',
      'What are the key takeaways to watch for?',
    ],
  },
  'paper-insight': {
    label: 'Paper Insights',
    icon: 'BookOpen',
    placeholder: 'Ask about this paper…',
    greeting: "I've analyzed this paper. I can explain the methodology, compare it with related work, identify key contributions, or discuss implications for your research. What would you like to explore?",
    suggestions: [
      'What are the key contributions?',
      'Explain the methodology in simple terms',
      'Compare with similar papers in this space',
      'What are the main limitations?',
    ],
  },
  report: {
    label: 'Report Builder',
    icon: 'FileText',
    placeholder: 'Ask AI to generate a report…',
    greeting: "Hi! I can organize your enabled conference activity into a shareable report. What would you like to create?",
    suggestions: [
      "Summarize today's keynote sessions",
      'Generate a report on AI Ethics discussions',
      'Create a summary of workshops attended',
      'List key insights from all sessions',
    ],
  },
  networking: {
    label: 'Networking',
    icon: 'Users',
    placeholder: 'Search names, companies, or interests…',
    greeting: 'Find attendees who share your interests. Matching stays within Rubikoo; directory profiles and private saved sessions are not sent to third-party AI providers.',
    suggestions: [
      'Show attendees with shared interests',
      'Show attendees at this event',
    ],
  },
  recap: {
    label: 'Session Recap',
    icon: 'Clock',
    placeholder: 'Ask for a recap of this session…',
    greeting: "I'm your AI Recap Assistant! I can summarize the key points of this session, highlight important takeaways, and provide a quick overview of the discussion.",
    suggestions: [
      'Summarize the key points of this session',
      'Highlight important takeaways',
      'Provide a quick overview of the discussion',
      'List any open questions or next steps',
    ],
  },
  agent: {
    label: 'Agent Analysis',
    icon: 'Bot',
    placeholder: 'Ask about the agent in this paper…',
    greeting: "I'm your AI Agent Analysis Assistant! I can explain the agent architecture, discuss its capabilities, and highlight any key contributions to the field.",
    suggestions: [
      'Explain the agent architecture',
      'Discuss its capabilities',
      'Highlight any key contributions to the field',
      'Compare with other agent architectures',
    ],
  },
} as const;

// ─── Main Entry Points ─────────────────────────────────────────────────────────

/**
 * generateAIResponse — main entry point for all AI interactions.
 *
 * For the FIRST message in session-prep, paper-insight, report, networking modes,
 * calls the dedicated API route for a richer structured response.
 * All other messages go through ai-chat (agentic, with tool calling).
 *
 * @param onProgress - optional callback for loading status updates
 * @param onStream   - optional callback for streaming text tokens (ai-chat only)
 */
export async function generateAIResponse(
  input: string, context: AIContext, onProgress?: (status: string) => void,
  onStream?: (token: string) => void, history: { role: string; content: string }[] = [],
  threadSummary?: string, signal?: AbortSignal,
): Promise<AIResponse> {
  try {
    if (context.mode === 'networking') return await generateAIResponseInternal(input, context, onProgress, onStream, [], undefined, signal);
    return await withAISharing(signal => generateAIResponseInternal(input, context, onProgress, onStream, history, threadSummary, signal), { signal });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    return formatCopilotError(error);
  }
}

async function generateAIResponseInternal(
  input: string,
  context: AIContext,
  onProgress?: (status: string) => void,
  onStream?: (token: string) => void,
  history: { role: string; content: string }[] = [],
  threadSummary?: string,
  signal?: AbortSignal,
): Promise<AIResponse> {
  if (onProgress) onProgress('Thinking…');

  const session = await authService.getSession();
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  if (!session) {
    return { content: 'Please sign in to use the AI Copilot.', chips: [] };
  }

  const isFirstMessage = !input || input.trim() === '';
  const mode = context.mode ?? 'general';

  try {
    // Directory matching remains local on every turn; never send its result
    // history/candidate profiles as a follow-up AI prompt.
    if (mode === 'networking') {
      const defaultListing = !input || MODE_CONFIG.networking.suggestions.some(suggestion => suggestion === input);
      const result = await callAPI<AIResponse>('/ai/networking', { eventId: context.eventId, query: defaultListing ? undefined : input }, signal);
      return { ...result, chips: [] };
    }
    // ── Specialized functions for quick insights ─────────────────────────────
    if (isFirstMessage && mode === 'session-prep' && context.sessionId) {
      return await callSessionPrep(context.sessionId, onProgress, signal);
    }

    if (isFirstMessage && mode === 'paper-insight' && context.paperId) {
      return await callPaperInsight(context.paperId, undefined, onProgress, context.eventId, signal);
    }

    if (isFirstMessage && mode === 'report' && context.eventId) {
      return await callReport(context.eventId, undefined, onProgress, signal);
    }

    // ── All other cases → ai-chat with agentic tool calling ──────────────────
    const message = input || getDefaultPrompt(mode, context);

    if (onStream) {
      if (onProgress) onProgress('Generating…');
      return await callAIChatStreaming(message, context, onStream, history, threadSummary, onProgress, signal);
    }

    if (onProgress) onProgress('Analyzing context…');
    return await callAIChat(message, context, history, threadSummary, signal);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    const reason = err instanceof ApiError ? err.code : (err as { reason?: string } | null)?.reason;
    if (reason === 'ai_sharing_required' || reason === 'ai_client_update_required') {
      invalidateAISharing();
      throw new AISharingRequired();
    }
    const message = err instanceof Error ? err.message : String(err);
    console.error('[aiService] generateAIResponse error:', message);

    return formatCopilotError(err);
  }
}

/**
 * getQuickInsight — fires a pre-set insight for a given context without user input.
 * Used by PaperDetailsScreen and SessionPrepScreen to auto-load analysis.
 */
export async function getQuickInsight(
  context: AIContext,
  onProgress?: (status: string) => void,
  signal?: AbortSignal,
): Promise<AIResponse> {
  try {
    if (context.mode === 'networking') return await generateAIResponseInternal('', context, onProgress, undefined, [], undefined, signal);
    return await withAISharing(signal => generateAIResponseInternal('', context, onProgress, undefined, [], undefined, signal), { signal, prompt: false });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    return formatCopilotError(error);
  }
}

/**
 * generateConferenceRecap — generates a full structured post-conference recap.
 * Calls the ai-recap API route.
 */
export async function generateConferenceRecap(
  eventId: string, onProgress?: (status: string) => void, signal?: AbortSignal,
): Promise<ConferenceRecap> {
  return withAISharing(signal => generateConferenceRecapInternal(eventId, onProgress, signal), { signal });
}

async function generateConferenceRecapInternal(
  eventId: string,
  onProgress?: (status: string) => void,
  signal?: AbortSignal,
): Promise<ConferenceRecap> {
  const steps = [
    'Analyzing your conference activity…',
    'Synthesizing session notes…',
    'Identifying emerging themes…',
    'Finding your key learnings…',
    'Writing your personal narrative…',
    'Finalizing your recap…',
  ];

  if (onProgress) {
    for (const step of steps) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      onProgress(step);
      await new Promise<void>((resolve, reject) => {
        const handleAbort = () => {
          clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        };
        const timer = setTimeout(() => {
          signal?.removeEventListener('abort', handleAbort);
          resolve();
        }, 350);
        signal?.addEventListener('abort', handleAbort, { once: true });
      });
    }
  }

  const session = await authService.getSession();
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  if (!session) throw new Error('Not authenticated');

  const response = await callAPI<unknown>('/ai/recap', { eventId }, signal);
  return normalizeConferenceRecapResponse(response);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function textList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && !!item.trim()).map(item => item.trim())
    : [];
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

/**
 * Convert the legacy FastAPI recap payload into the shared mobile contract.
 * Keeping this normalization at the API boundary prevents malformed or older
 * responses from reaching the screen and causing a render-time crash.
 */
export function normalizeConferenceRecapResponse(raw: unknown): ConferenceRecap {
  const payload = isRecord(raw) ? raw : {};
  const recap = isRecord(payload.recap) ? payload.recap : {};
  const stats = isRecord(payload.stats) ? payload.stats : {};
  const event = isRecord(payload.event) ? payload.event : {};
  const eventName = text(event.name, 'Conference');
  const intro = text(
    recap.intro,
    'Your conference recap is ready. Bookmark sessions and take notes to make it more personal.',
  );
  const highlights = textList(recap.highlights);
  const themes = textList(recap.themes);
  const learnings = textList(recap.learnings);
  const nextSteps = textList(recap.next_steps);

  const sections: ConferenceRecap['sections'] = [
    {
      id: 'summary',
      title: 'Your Conference Story',
      icon: 'Sparkles',
      type: 'summary',
      content: intro,
    },
  ];

  const addListSection = (
    id: string,
    title: string,
    icon: string,
    type: ConferenceRecap['sections'][number]['type'],
    items: string[],
  ) => {
    if (!items.length) return;
    sections.push({ id, title, icon, type, content: items.map(item => `- ${item}`).join('\n') });
  };

  addListSection('highlights', 'Conference Highlights', 'FileText', 'insights', highlights);
  addListSection('themes', 'Themes You Explored', 'Sparkles', 'insights', themes);
  addListSection('learnings', 'Key Learnings', 'BookOpen', 'insights', learnings);

  if (nextSteps.length) {
    sections.push({
      id: 'next-steps',
      title: 'Next Steps',
      icon: 'CheckSquare',
      type: 'actions',
      content: 'Turn your conference insights into action.',
      items: nextSteps.map((item, index) => ({
        id: `next-step-${index + 1}`,
        text: item,
        completed: false,
      })),
    });
  }

  return {
    title: `${eventName} Recap`,
    receiptId: text(payload.receipt_id) || undefined,
    subtitle: 'Your personalized conference highlights',
    generatedAt: text(payload.generatedAt, new Date().toISOString()),
    stats: {
      sessionsAttended: count(stats.sessions),
      notesCreated: count(stats.notes),
      connectionsMade: count(stats.connections),
      papersReviewed: count(stats.papers),
    },
    sections,
    quote: text(recap.quote) || undefined,
    event: {
      name: eventName,
      location: text(event.location) || undefined,
    },
  };
}

/**
 * embedEventContent — triggers batch embedding for all content in an event.
 * Called from the admin portal after content is ready.
 */
export async function embedEventContent(
  eventId: string,
  types?: ('session' | 'speaker' | 'exhibitor')[],
): Promise<{ success: boolean; embedded: Record<string, number>; total: number }> {
  const session = await authService.getSession();
  if (!session) throw new Error('Not authenticated');

  return callAPI<{ success: boolean; embedded: Record<string, number>; total: number }>('/admin/embed-content', { eventId, types });
}

// ─── Action Chips + Suggestions ───────────────────────────────────────────────

type ModuleCheck = (key: EventModuleKey) => boolean;
type ContextSuggestion = {
  label: string;
  prompt: string;
  icon: string;
  requires?: EventModuleKey[];
};

export function getAvailableAIModes(isEnabled: ModuleCheck): AIMode[] {
  return (['general', 'report', 'paper-insight', 'session-prep', 'networking'] as AIMode[])
    .filter((mode) => (
      mode === 'paper-insight' ? isEnabled('papers')
        : mode === 'session-prep' ? isEnabled('agenda')
          : mode === 'networking' ? isEnabled('attendees')
            : true
    ));
}

export function getAIModeSuggestions(mode: AIMode, isEnabled: ModuleCheck): string[] {
  const requirements: Partial<Record<AIMode, EventModuleKey[][]>> = {
    general: [['agenda'], ['attendees'], [], ['agenda']],
    report: [['agenda'], ['agenda'], ['agenda'], ['agenda']],
    'session-prep': [[], ['speakers'], ['papers'], []],
    networking: [[], [], [], ['agenda']],
  };
  const config = MODE_CONFIG[mode];
  const available = config.suggestions.filter((_, index) => (
    (requirements[mode]?.[index] ?? []).every(isEnabled)
  ));
  return available.length > 0
    ? [...available]
    : ['Ask what insights are available for this event'];
}

export function filterActionChips(
  chips: ActionChip[] | undefined,
  isEnabled: ModuleCheck,
): ActionChip[] {
  const actionModules: Record<string, EventModuleKey> = {
    schedule: 'agenda',
    network: 'attendees',
    'view-matches': 'attendees',
    related: 'papers',
    'more-questions': 'speakers',
    'take-notes': 'notes',
    save: 'notes',
    'save-note': 'notes',
  };
  return (chips ?? []).filter((chip) => {
    const required = actionModules[chip.action];
    return !required || isEnabled(required);
  });
}

export function getContextSuggestions(
  screen: string,
  isEnabled: ModuleCheck = () => true,
): { label: string; prompt: string; icon: string }[] {
  const suggestions: Record<string, ContextSuggestion[]> = {
    home: [
      { label: 'Plan my day', prompt: 'Help me plan my conference schedule for today', icon: 'Calendar', requires: ['agenda'] },
      { label: 'Key sessions', prompt: 'What are the must-attend sessions today?', icon: 'Zap', requires: ['agenda'] },
      { label: 'Who to meet', prompt: 'Who should I network with today?', icon: 'Users', requires: ['attendees'] },
    ],
    agenda: [
      { label: 'Summarize schedule', prompt: 'Summarize my schedule for today', icon: 'FileText' },
      { label: 'Prep next session', prompt: 'Help me prepare for my next upcoming session', icon: 'Zap' },
      { label: 'Session conflicts', prompt: 'Are there any scheduling conflicts I should know about?', icon: 'AlertCircle' },
    ],
    library: [
      { label: 'Summarize saves', prompt: 'Summarize the key themes from my saved papers and sessions', icon: 'FileText' },
      { label: 'Generate report', prompt: 'Generate a conference report from my saved items', icon: 'FileText' },
      { label: 'Find gaps', prompt: 'What topics am I missing from my saved content?', icon: 'Search' },
    ],
    explore: [
      { label: 'Trending now', prompt: 'What are the trending topics at this conference?', icon: 'TrendingUp' },
      { label: 'Paper recs', prompt: 'Recommend papers based on my interests', icon: 'BookOpen', requires: ['papers'] },
      { label: 'Networking tips', prompt: 'Give me networking tips for this conference', icon: 'Users', requires: ['attendees'] },
    ],
    session: [
      { label: 'Prep brief', prompt: 'Generate a preparation brief for this session', icon: 'Zap' },
      { label: 'Questions', prompt: 'Generate smart questions to ask during this session', icon: 'MessageCircle' },
      { label: 'Take notes', prompt: 'Help me take structured notes for this session', icon: 'FileText', requires: ['notes'] },
    ],
    paper: [
      { label: 'Key insights', prompt: 'What are the key contributions of this paper?', icon: 'Sparkles' },
      { label: 'Explain simply', prompt: 'Explain this paper methodology in simple terms', icon: 'BookOpen' },
      { label: 'Related work', prompt: 'Find related papers I should read', icon: 'BookOpen' },
    ],
    attendee: [
      { label: 'Icebreaker', prompt: 'Generate an icebreaker for connecting with this person', icon: 'MessageCircle' },
      { label: 'Common ground', prompt: 'What do we have in common based on our profiles?', icon: 'Users' },
      { label: 'Shared sessions', prompt: 'Find sessions we are both interested in', icon: 'Calendar', requires: ['agenda'] },
    ],
  };

  const candidates = suggestions[screen] ?? [
    { label: 'Ask anything', prompt: 'What can you help me with at this conference?', icon: 'Sparkles' },
    { label: 'Recommend', prompt: 'Give me personalized recommendations', icon: 'Zap' },
    { label: 'Summarize', prompt: 'Summarize the key highlights so far', icon: 'FileText' },
  ];
  const available = candidates.filter((suggestion) => (
    (suggestion.requires ?? []).every(isEnabled)
  ));
  if (available.length > 0) {
    return available.map(({ requires: _requires, ...suggestion }) => suggestion);
  }
  return [{
    label: 'Ask about this event',
    prompt: 'What can you help me with at this conference?',
    icon: 'Sparkles',
  }];
}

export function getAgentActionDescription(type: string, metadata?: Record<string, string>): string {
  switch (type) {
    case 'schedule-meeting':
      return `Drafting a coffee invite${metadata?.name ? ` for ${metadata.name}` : ''} — saved to My Notes`;
    case 'draft-followup':
      return `Drafting a follow-up message${metadata?.name ? ` for ${metadata.name}` : ''} — saved to My Notes`;
    case 'block-time':
      return `Saving a ${metadata?.duration || '30'}-minute "${metadata?.topic || 'review'}" plan to My Notes`;
    case 'add-linkedin':
      return `Saving a LinkedIn reminder${metadata?.name ? ` for ${metadata.name}` : ''} to My Notes`;
    case 'send-intro':
      return `Drafting an introduction message — saved to My Notes`;
    case 'save-reading-list':
      return `Saving this paper to your reading list`;
    case 'export-recap':
      return `Saving your conference recap to My Notes`;
    default:
      return `Executing: ${type}`;
  }
}

/**
 * draftAgentMessage — generate a real, ready-to-send message via the agentic
 * chat endpoint. Backs the Agent Actions ("Draft follow-up", "Draft coffee
 * invite") so they produce genuine, personalized text the user can review and
 * send — instead of a fake success. The agent looks the recipient up by ID via
 * its own tools, so the draft is grounded in the actual attendee profile.
 */
export async function draftAgentMessage(
  kind: 'follow-up' | 'coffee-invite' | 'intro',
  ctx: { entityId?: string; eventId?: string; name?: string }, options: ApiRequestOptions = {},
): Promise<string> {
  return withAISharing(signal => draftAgentMessageInternal(kind, ctx, { signal }), options);
}

async function draftAgentMessageInternal(
  kind: 'follow-up' | 'coffee-invite' | 'intro',
  ctx: { entityId?: string; eventId?: string; name?: string },
  options: ApiRequestOptions = {},
): Promise<string> {
  const session = await authService.getSession();
  if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  if (!session) throw new Error('Please sign in to use AI actions.');

  const intent: Record<'follow-up' | 'coffee-invite' | 'intro', string> = {
    'follow-up': 'a warm, concise follow-up message to send after meeting this person at the conference',
    'coffee-invite': 'a short, friendly message inviting this person to grab coffee at the conference',
    intro: 'a brief, warm introduction message to this person',
  };

  // Recipient profiles are not shared with AI; the user can personalize this
  // generic draft themselves. Only use the consenting caller's own context.
  const lookup = 'Use a generic recipient without a name or profile lookup. Call get_user_context to personalize from my own profile.';

  const message =
    `Draft ${intent[kind]}. ${lookup} ` +
    'Keep it to 2-3 sentences, specific and genuine. ' +
    'Return ONLY the message text — no preamble, surrounding quotes, or subject line.';

  const res = await apiClient.post<{ content: string }>(
    '/ai/chat',
    {
      message,
      context: { mode: 'general', eventId: ctx.eventId ?? null },
      stream: false,
    },
    aiSharingRequestOptions(options.signal),
  );
  const text = (res?.content ?? '').trim();
  if (!text) throw new Error('The AI could not draft a message. Please try again.');
  return text;
}

// ─── Private Helpers ───────────────────────────────────────────────────────────

/**
 * Call a FastAPI route via apiClient.
 * apiClient handles auth headers and error propagation automatically.
 */
async function callAPI<T = Record<string, unknown>>(
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  return apiClient.post<T>(path, body, aiSharingRequestOptions(signal));
}

async function callAIChat(
  message: string,
  context: AIContext,
  history: { role: string; content: string }[] = [],
  threadSummary?: string,
  signal?: AbortSignal,
): Promise<AIResponse> {
  const data = await callAPI<AIResponse & { receipt_id?: string }>(
    '/ai/chat',
    { message, context, thread_id: context.threadId ?? null, stream: false },
    signal,
  );
  return { ...data, receiptId: data.receipt_id };
}

async function callAIChatStreaming(
  message: string,
  context: AIContext,
  onToken: (token: string) => void,
  history: { role: string; content: string }[] = [],
  threadSummary?: string,
  onProgress?: (status: string) => void,
  signal?: AbortSignal,
): Promise<AIResponse> {
  const response = await apiClient.stream(
    '/ai/chat',
    { message, context, thread_id: context.threadId ?? null, stream: true },
    aiSharingRequestOptions(signal),
  );

  if (!response.body) {
    throw new Error(`Streaming error: no response body`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let fullContent = '';
  let receiptId: string | undefined;
  let buffer = '';
  let sharingRevoked = false;

  const consumeLine = (line: string) => {
    if (!line.startsWith('data: ')) return;
    const payload = line.slice(6).trim();
    if (payload === '[DONE]') return;
    try {
      const parsed = JSON.parse(payload);
      if (parsed.error === 'ai_sharing_required') {
        invalidateAISharing();
        sharingRevoked = true;
        return;
      }
      if (typeof parsed.receipt_id === 'string') {
        receiptId = parsed.receipt_id;
        return;
      }
      if (parsed.status) {
        onProgress?.(parsed.status);
        return;
      }
      const token = parsed.choices?.[0]?.delta?.content ?? '';
      if (token) {
        fullContent += token;
        onToken(token);
      }
    } catch { /* ignore malformed events */ }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (sharingRevoked) throw new DOMException('AI sharing changed', 'AbortError');
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    lines.forEach(consumeLine);
  }
  buffer += decoder.decode();
  if (buffer) consumeLine(buffer);

  return {
    content: fullContent,
    chips: getDefaultChips(context.mode ?? 'general'),
    receiptId,
  };
}

async function callSessionPrep(
  sessionId: string,
  onProgress?: (status: string) => void,
  signal?: AbortSignal,
): Promise<AIResponse> {
  if (onProgress) onProgress('Fetching session details…');
  const data = await callAPI('/ai/session-prep', { sessionId }, signal);
  if (onProgress) onProgress('Generating prep brief…');
  return {
    content: data.content as string,
    receiptId: data.receipt_id as string | undefined,
    chips: [
      { label: 'More Questions', icon: 'MessageCircle', action: 'more-questions' },
      { label: 'Related Papers', icon: 'BookOpen', action: 'related' },
      { label: 'Take Notes', icon: 'FileText', action: 'take-notes' },
      { label: 'Save Brief', icon: 'Bookmark', action: 'save' },
    ],
    metadata: data.session as Record<string, unknown>,
  };
}

async function callPaperInsight(
  paperId: string,
  question: string | undefined,
  onProgress?: (status: string) => void,
  eventId?: string,
  signal?: AbortSignal,
): Promise<AIResponse> {
  if (onProgress) onProgress('Analyzing paper…');
  const data = await callAPI('/ai/paper-insight', { paperId, question, eventId }, signal);
  if (onProgress) onProgress('Finding related work…');
  return {
    content: data.content as string,
    receiptId: data.receipt_id as string | undefined,
    chips: [
      { label: 'Ask Follow-up', icon: 'MessageCircle', action: 'follow-up' },
      { label: 'Related Papers', icon: 'BookOpen', action: 'related' },
      { label: 'Save Insight', icon: 'Bookmark', action: 'save' },
      { label: 'Share', icon: 'Share2', action: 'share' },
    ],
    metadata: { paper: data.paper, relatedPapers: data.relatedPapers } as Record<string, unknown>,
  };
}

async function callReport(
  eventId: string,
  focus: string | undefined,
  onProgress?: (status: string) => void,
  signal?: AbortSignal,
): Promise<AIResponse> {
  if (onProgress) onProgress('Gathering your conference activity…');
  const data = await callAPI('/ai/report', { eventId, focus }, signal);
  if (onProgress) onProgress('Writing your report…');
  return {
    content: data.content as string,
    receiptId: data.receipt_id as string | undefined,
    chips: [
      { label: 'Export PDF', icon: 'Download', action: 'export' },
      { label: 'Share', icon: 'Share2', action: 'share' },
      { label: 'Refine Focus', icon: 'RefreshCw', action: 'refine' },
    ],
    metadata: data.meta as Record<string, unknown>,
  };
}

async function callNetworking(
  eventId: string,
  query: string | undefined,
  onProgress?: (status: string) => void,
  signal?: AbortSignal,
): Promise<AIResponse> {
  if (onProgress) onProgress('Finding people with shared interests…');
  const data = await callAPI('/ai/networking', { eventId, query }, signal);

  const suggestions = data.suggestions as Array<{
    name: string;
    title?: string;
    company?: string;
    reason: string;
    icebreaker: string;
    sharedSessions: string[];
  }>;

  if (!suggestions?.length) {
    return {
      content: data.content as string || 'No matching attendees found',
      receiptId: data.receipt_id as string | undefined,
      chips: [{ label: 'Refresh', icon: 'RefreshCw', action: 'retry' }],
    };
  }

  const content = suggestions.map((s) => {
    const shared = s.sharedSessions?.length
      ? `\n  *Shared sessions:* ${s.sharedSessions.join(', ')}`
      : '';
    return `### ${s.name}\n*${s.title ?? ''} at ${s.company ?? ''}*\n\n**Why connect:** ${s.reason}${shared}\n\n**Icebreaker:** "${s.icebreaker}"`;
  }).join('\n\n---\n\n');

  return {
    content: data.content as string || `## Recommended Connections\n\n${content}`,
    receiptId: data.receipt_id as string | undefined,
    chips: [
      { label: 'Refine Search', icon: 'Search', action: 'refine' },
      { label: 'More Suggestions', icon: 'Users', action: 'more' },
    ],
    metadata: { suggestions } as Record<string, unknown>,
  };
}

function getDefaultChips(mode: string): ActionChip[] {
  const chips: Record<string, ActionChip[]> = {
    general: [
      { label: 'My Schedule', icon: 'Calendar', action: 'schedule' },
      { label: 'Who to Meet', icon: 'Users', action: 'network' },
      { label: 'Save Note', icon: 'FileText', action: 'save-note' },
    ],
    'session-prep': [
      { label: 'More Questions', icon: 'MessageCircle', action: 'more-questions' },
      { label: 'Related Papers', icon: 'BookOpen', action: 'related' },
    ],
    'paper-insight': [
      { label: 'Ask Follow-up', icon: 'MessageCircle', action: 'follow-up' },
      { label: 'Related Papers', icon: 'BookOpen', action: 'related' },
    ],
    report: [
      { label: 'Export PDF', icon: 'Download', action: 'export' },
      { label: 'Share', icon: 'Share2', action: 'share' },
    ],
    networking: [
      { label: 'More Suggestions', icon: 'Users', action: 'more' },
    ],
    recap: [
      { label: 'Export Report', icon: 'Download', action: 'export' },
      { label: 'Share Recap', icon: 'Share2', action: 'share' },
    ],
    agent: [
      { label: 'Ask Follow-up', icon: 'MessageCircle', action: 'follow-up' },
      { label: 'Save Note', icon: 'FileText', action: 'save-note' },
    ],
  };
  return chips[mode] ?? chips.general;
}

function getDefaultPrompt(mode: string, context: AIContext): string {
  const defaults: Record<string, string> = {
    'paper-insight': 'What are the key contributions of this paper?',
    'session-prep': 'Generate a preparation brief for this session.',
    report: 'Generate a conference report based on my attended sessions.',
    networking: 'Who should I network with based on my research interests?',
    recap: 'Summarize the key points and highlights of my conference experience.',
    agent: 'Explain the AI agent architecture described in this paper.',
    general: 'What are the highlights and trending topics of this conference?',
  };
  const base = defaults[mode] ?? defaults.general;

  // Inject entity context into default prompt
  if (context.sessionId) return `${base} (Session ID: ${context.sessionId})`;
  if (context.paperId) return `${base} (Paper ID: ${context.paperId})`;
  if (context.eventId) return `${base} (Event ID: ${context.eventId})`;
  return base;
}
